import { useState, useMemo, useEffect } from "react";
import { ChevronDown, ChevronUp, ExternalLink, ArrowUpDown, Award, Medal, Trophy, Download, Link2, Star } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSearchExport } from "@/hooks/useSearchExport";
import { useFavorites } from "@/hooks/useFavorites";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import MobileResultCard from "@/components/mobile/MobileResultCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchResult } from "@/types/search";
import { toast } from "sonner";

interface SearchResultsProps {
  results: SearchResult[];
  selectedPlatforms: string[];
  hideOutOfStock: boolean;
  selectedBrands: string[];
  searchType: 'oem' | 'ref';
  searchQuery: string;
}

const normalizeRef = (ref: string, brand?: string): string => {
  if (!ref) return "N/A";
  ref = ref.trim().toUpperCase();
  if (brand && ref.startsWith(brand + " ")) {
    return ref.substring(brand.length + 1).trim();
  }
  return ref;
};

const SearchResults = ({ results, selectedPlatforms, hideOutOfStock, selectedBrands, searchType, searchQuery }: SearchResultsProps) => {
  const isMobile = useIsMobile();
  const { isEnabled: isExportEnabled } = useSearchExport();
  const { user } = useAuth();
  const { addFavorite, removeFavorite, isFavorite, getFavoriteId } = useFavorites();
  const [expandedRefs, setExpandedRefs] = useState<Set<string>>(new Set());
  const [addingFavorite, setAddingFavorite] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"price-asc" | "price-desc" | "brand" | "platform">(() => {
    const saved = localStorage.getItem('searchSortPreference');
    return (saved as "price-asc" | "price-desc" | "brand" | "platform") || "price-asc";
  });
  const [showTop3Only, setShowTop3Only] = useState(false);
  const [showAllResults, setShowAllResults] = useState(false);
  const [resultsPerPage, setResultsPerPage] = useState<number>(() => {
    const saved = localStorage.getItem('searchResultsPerPage');
    return saved ? Number(saved) : 12;
  });

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('searchSortPreference', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('searchResultsPerPage', resultsPerPage.toString());
  }, [resultsPerPage]);


  const filteredResults = useMemo(() => {
    try {
      return results.filter(prod => {
        // Safety check for required fields
        if (!prod || !prod.platform) {
          console.warn('[SearchResults] Skipping invalid product:', prod);
          return false;
        }
        const platformMatch = selectedPlatforms.includes(prod.platform);
        const stockMatch = !hideOutOfStock || prod.isInStock;
        const brandMatch = selectedBrands.length === 0 || selectedBrands.includes(prod.brand || "");
        return platformMatch && stockMatch && brandMatch;
      });
    } catch (error) {
      console.error('[SearchResults] Error filtering results:', error);
      return [];
    }
  }, [results, selectedPlatforms, hideOutOfStock, selectedBrands]);

  // Reset showAllResults only when platforms or brands change, not when stock filter changes
  useEffect(() => {
    setShowAllResults(false);
  }, [selectedPlatforms, selectedBrands]);

  // Tri global d'abord sur TOUS les résultats
  const sortedResults = useMemo(() => {
    try {
      const sorted = [...filteredResults];
      sorted.sort((a, b) => {
        if (sortBy === "price-asc") {
          const priceA = a.price?.current?.price ?? Infinity;
          const priceB = b.price?.current?.price ?? Infinity;
          // Handle NaN and invalid numbers
          const safeA = isNaN(priceA) ? Infinity : priceA;
          const safeB = isNaN(priceB) ? Infinity : priceB;
          return safeA - safeB;
        } else if (sortBy === "price-desc") {
          const priceA = a.price?.current?.price ?? -Infinity;
          const priceB = b.price?.current?.price ?? -Infinity;
          const safeA = isNaN(priceA) ? -Infinity : priceA;
          const safeB = isNaN(priceB) ? -Infinity : priceB;
          return safeB - safeA;
        } else if (sortBy === "brand") {
          return (a.brand || "").localeCompare(b.brand || "");
        } else if (sortBy === "platform") {
          return (a.platform || "").localeCompare(b.platform || "");
        }
        return 0;
      });
      return sorted;
    } catch (error) {
      console.error('[SearchResults] Error sorting results:', error);
      return filteredResults;
    }
  }, [filteredResults, sortBy]);

  const groupedResults = useMemo(() => {
    const groups: { [key: string]: SearchResult[] } = {};
    sortedResults.forEach(prod => {
      const normRef = normalizeRef(prod.refNumber, prod.brand);
      if (!groups[normRef]) {
        groups[normRef] = [];
      }
      groups[normRef].push(prod);
    });
    return groups;
  }, [sortedResults]);

  // Map pour récupérer l'index global de chaque produit
  const globalIndexMap = useMemo(() => {
    const map = new Map<SearchResult, number>();
    sortedResults.forEach((prod, index) => {
      map.set(prod, index);
    });
    return map;
  }, [sortedResults]);

  const getBestDealBadge = (index: number) => {
    if (index === 0) {
      return (
        <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30 hover:bg-yellow-500/20 flex items-center gap-1 animate-pulse">
          <Trophy className="h-3 w-3" />
          <span className="font-bold">1er</span>
        </Badge>
      );
    }
    if (index === 1) {
      return (
        <Badge className="bg-gray-400/20 text-gray-600 border-gray-400/30 hover:bg-gray-400/20 flex items-center gap-1">
          <Medal className="h-3 w-3" />
          <span className="font-bold">2ème</span>
        </Badge>
      );
    }
    if (index === 2) {
      return (
        <Badge className="bg-orange-600/20 text-orange-600 border-orange-600/30 hover:bg-orange-600/20 flex items-center gap-1">
          <Award className="h-3 w-3" />
          <span className="font-bold">3ème</span>
        </Badge>
      );
    }
    return null;
  };

  const toggleExpand = (ref: string) => {
    setExpandedRefs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ref)) {
        newSet.delete(ref);
      } else {
        newSet.add(ref);
      }
      return newSet;
    });
  };

  const handleToggleFavorite = async (product: SearchResult, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Create unique key
    const key = `${product.platform}-${product.title}-${product.refNumber}-${product.brand}`;
    
    // Prevent multiple clicks
    if (addingFavorite === key) return;
    
    // Check if already favorite
    const favoriteId = getFavoriteId(product.platform, product.title, product.refNumber);
    
    setAddingFavorite(key);
    
    if (favoriteId) {
      // Remove from favorites
      await removeFavorite(favoriteId);
    } else {
      // Add to favorites
      await addFavorite(
        searchQuery,
        searchType,
        product.platform,
        product.title,
        product.websiteUrl || null,
        product.price?.current?.price || null,
        product.brand || null,
        product.refNumber || null
      );
    }
    
    setAddingFavorite(null);
  };

  const handleExportResults = () => {
    console.log('Export triggered with searchType:', searchType, 'searchQuery:', searchQuery);
    console.log('Filtered results count:', filteredResults.length);
    
    const exportData = filteredResults.map((result) => ({
      searchType: searchType,
      reference: searchQuery, // Référence de recherche (OEM/fabricant)
      productRef: result.refNumber, // Référence du produit
      platform: result.platform,
      brand: result.brand,
      title: result.title,
      price: result.price?.current?.price || 0,
      isInStock: result.isInStock,
      websiteUrl: result.websiteUrl || '#demo',
    }));

    console.log('Export data prepared:', exportData);

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `search-results-${searchType}-${searchQuery}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success(`${exportData.length} résultats exportés pour la référence ${searchQuery}`);
  };

  if (filteredResults.length === 0) {
    return (
      <div className={isMobile ? "p-6 text-center" : "glass-effect p-8 rounded-lg text-center"}>
        <p className="text-muted-foreground">Aucun résultat trouvé</p>
      </div>
    );
  }

  // Calculate display results with pagination
  let displayResults = showTop3Only ? sortedResults.slice(0, 3) : sortedResults;
  const paginatedResults = showAllResults ? displayResults : displayResults.slice(0, resultsPerPage);
  const hasMoreResults = displayResults.length > resultsPerPage;

  if (isMobile) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-center gap-2">
            <h3 className="text-lg font-bold">{filteredResults.length} résultats</h3>
            <div className="flex items-center gap-2">
              {isExportEnabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportResults}
                  className="h-9 px-3 text-xs font-semibold"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Exporter
                </Button>
              )}
              <Button
                variant={showTop3Only ? "default" : "outline"}
                size="sm"
                onClick={() => setShowTop3Only(!showTop3Only)}
                className="h-9 px-3 text-xs font-semibold"
              >
                <Trophy className="h-4 w-4 mr-1" />
                Top 3
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Trier par:</span>
            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
              <SelectTrigger className="w-[100px] h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="price-asc">Prix ↑</SelectItem>
                <SelectItem value="price-desc">Prix ↓</SelectItem>
                <SelectItem value="brand">Marque</SelectItem>
                <SelectItem value="platform">Plateforme</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground whitespace-nowrap">Par page:</span>
            <Select value={resultsPerPage.toString()} onValueChange={(value) => setResultsPerPage(Number(value))}>
              <SelectTrigger className="w-[80px] h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="12">12</SelectItem>
                <SelectItem value="24">24</SelectItem>
                <SelectItem value="48">48</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-3">
          {paginatedResults.map((product, idx) => {
            const key = `${product.platform}-${product.title}-${product.refNumber}-${product.brand}`;
            return (
              <div key={key} className="relative">
                {getBestDealBadge(idx) && (
                  <div className="absolute -top-2 -right-2 z-10">
                    {getBestDealBadge(idx)}
                  </div>
                )}
                <MobileResultCard 
                  result={product}
                  onAddFavorite={user ? (result) => handleToggleFavorite(result) : undefined}
                  isFavorite={user ? isFavorite(product.platform, product.title, product.refNumber) : false}
                  isAddingFavorite={addingFavorite === key}
                />
              </div>
            );
          })}
        </div>
        {hasMoreResults && !showAllResults && (
          <div className="flex justify-center pt-4">
            <Button
              variant="outline"
              onClick={() => setShowAllResults(true)}
              className="w-full sm:w-auto"
            >
              Voir tous les produits ({displayResults.length})
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Desktop: Use mobile-style cards in grid layout
  
  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-lg font-bold text-foreground">
            {filteredResults.length} résultats
          </h3>
          
          <div className="flex items-center gap-2">
            {isExportEnabled && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportResults}
                className="h-9 px-3 text-xs font-semibold"
              >
                <Download className="h-4 w-4 mr-1" />
                Exporter
              </Button>
            )}
            <Button
              variant={showTop3Only ? "default" : "outline"}
              size="sm"
              onClick={() => setShowTop3Only(!showTop3Only)}
              className="h-9 px-3 text-xs font-semibold"
            >
              <Trophy className="h-4 w-4 mr-1" />
              Top 3
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Trier par:</span>
          <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
            <SelectTrigger className="w-[140px] h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="price-asc">Prix croissant</SelectItem>
              <SelectItem value="price-desc">Prix décroissant</SelectItem>
              <SelectItem value="brand">Marque (A-Z)</SelectItem>
              <SelectItem value="platform">Plateforme (A-Z)</SelectItem>
            </SelectContent>
          </Select>
          
          <span className="text-xs text-muted-foreground">Par page:</span>
          <Select value={resultsPerPage.toString()} onValueChange={(value) => setResultsPerPage(Number(value))}>
            <SelectTrigger className="w-[80px] h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="12">12</SelectItem>
              <SelectItem value="24">24</SelectItem>
              <SelectItem value="48">48</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile-style cards in responsive grid for desktop */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {paginatedResults.length === 0 ? (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            Aucun résultat trouvé
          </div>
        ) : (
          paginatedResults.map((product, idx) => {
            const key = `${product.platform}-${product.title}-${product.refNumber}-${product.brand}`;
            return (
              <div key={key} className="relative">
                {getBestDealBadge(idx) && (
                  <div className="absolute -top-2 -right-2 z-10">
                    {getBestDealBadge(idx)}
                  </div>
                )}
                <MobileResultCard 
                  result={product}
                  onAddFavorite={user ? (result) => handleToggleFavorite(result) : undefined}
                  isFavorite={user ? isFavorite(product.platform, product.title, product.refNumber) : false}
                  isAddingFavorite={addingFavorite === key}
                />
              </div>
            );
          })
        )}
      </div>

      {hasMoreResults && !showAllResults && (
        <div className="flex justify-center pt-6">
          <Button
            variant="outline"
            onClick={() => setShowAllResults(true)}
            className="min-w-[200px]"
          >
            Voir tous les produits ({displayResults.length})
          </Button>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
